import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, MapPin, Phone, Mail } from 'lucide-react';
import Button from '../ui/Button';

interface BookingDetails {
  id: number;
  service_id: number;
  start_time: string;
  end_time: string;
  status: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  notes?: string;
  created_at: string;
  practitioners: Array<{id: number; name: string}>;
  service?: {
    name: string;
    description?: string;
  };
}

interface BookingConfirmationProps {
  booking: BookingDetails;
  onGoHome?: () => void;
}

interface Service {
  id: number;
  name: string;
  description?: string;
  duration_minutes: number;
  requires_practitioners: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
}

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({
  booking,
  onGoHome,
}) => {
  const [serviceName, setServiceName] = React.useState<string>('Service réservé');

  // Booking object tracking removed for production

  React.useEffect(() => {
    const fetchServiceName = async () => {
      try {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
        const response = await fetch(`${apiBaseUrl}/api/services`);
        
        if (response.ok) {
          const services: Service[] = await response.json();
          const service = services.find(s => s.id === booking.service_id);
          if (service) {
            setServiceName(service.name);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du service:', error);
      }
    };

    // Priorité 1: Utiliser le nom du service depuis l'objet booking si disponible
    if (booking.service?.name) {
      setServiceName(booking.service.name);
    } 
    // Priorité 2: Récupérer depuis l'API si service_id disponible
    else if (booking.service_id) {
      fetchServiceName();
    }
  }, [booking.service_id, booking.service?.name]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Date à confirmer';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date à confirmer';
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return 'Heure à confirmer';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Heure à confirmer';
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const addToCalendar = () => {
    const startDate = new Date(booking.start_time);
    const endDate = new Date(booking.end_time);
    
    const formatDateForCalendar = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const practitionerName = booking.practitioners.length > 0 ? booking.practitioners[0].name : 'À confirmer';

    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(serviceName)}&dates=${formatDateForCalendar(startDate)}/${formatDateForCalendar(endDate)}&details=${encodeURIComponent(`Réservation confirmée chez Doux ce Moman\n\nService: ${serviceName}\nPraticien: ${practitionerName}\n\nNuméro de réservation: ${booking.id}`)}&location=${encodeURIComponent('Doux ce Moman - Institut de bien-être')}`;
    
    window.open(calendarUrl, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-2xl shadow-lg p-8">
        {/* Header de confirmation */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6"
          >
            <CheckCircle className="text-green-600" size={40} />
          </motion.div>
          
          <h2 className="font-playfair text-3xl font-bold text-neutral-ink mb-4">
            Réservation confirmée !
          </h2>
          
          <p className="text-neutral-body text-lg mb-2">
            Votre rendez-vous a été enregistré avec succès
          </p>
        </div>

        {/* Détails de la réservation */}
        <div className="bg-neutral-offwhite rounded-xl p-6 mb-8">
          <h3 className="font-playfair font-bold text-neutral-ink mb-6 text-xl">
            Détails de votre rendez-vous
          </h3>
          
          <div className="space-y-4">
            {/* Service */}
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-brand rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm">✨</span>
              </div>
              <div>
                <p className="font-semibold text-neutral-ink">{serviceName}</p>
              </div>
            </div>

            {/* Date */}
            <div className="flex items-start space-x-3">
              <Calendar className="text-brand mt-1" size={20} />
              <div>
                <p className="font-semibold text-neutral-ink">
                  {formatDate(booking.start_time)}
                </p>
                <p className="text-neutral-body text-sm">
                  {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                </p>
              </div>
            </div>

            {/* Lieu */}
            <div className="flex items-start space-x-3">
              <MapPin className="text-brand mt-1" size={20} />
              <div>
                <p className="font-semibold text-neutral-ink">Doux ce Moman</p>
                <p className="text-neutral-body text-sm">Institut de bien-être</p>
              </div>
            </div>
          </div>
        </div>

        {/* Informations importantes */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
          <h3 className="font-playfair font-bold text-blue-800 mb-4">
            Informations importantes
          </h3>
          
          <div className="space-y-2 text-blue-700 text-sm">
            <p>• Merci d'arriver 10 minutes avant votre rendez-vous</p>
            <p>• En cas d'empêchement, prévenez-nous au moins 48h à l'avance</p>
            {booking.customer_email && (
              <p>• Un email de confirmation vous a été envoyé à {booking.customer_email}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          {/* Bouton principal - Ajouter au calendrier */}
          <Button
            variant="primary"
            onClick={addToCalendar}
            className="w-full flex items-center justify-center space-x-2"
          >
            <Calendar size={20} />
            <span>Ajouter à mon calendrier</span>
          </Button>

          {/* Actions secondaires */}
          <div className="flex justify-center">
            {onGoHome && (
              <Button
                variant="secondary"
                onClick={onGoHome}
                className="flex items-center justify-center space-x-2"
              >
                <span>Retour à l'accueil</span>
              </Button>
            )}
          </div>
        </div>

        {/* Contact */}
        <div className="mt-8 pt-6 border-t border-neutral-line text-center">
          <p className="text-neutral-body text-sm mb-4">
            Une question ? Contactez-nous
          </p>
          
          <div className="flex justify-center space-x-6 text-sm">
            <a 
              href="tel:+33123456789" 
              className="flex items-center space-x-1 text-brand hover:underline"
            >
              <Phone size={16} />
              <span>06 28 97 99 07</span>
            </a>
            
            <a 
              href="mailto:contact@douxcemoman.fr" 
              className="flex items-center space-x-1 text-brand hover:underline"
            >
              <Mail size={16} />
              <span>contact@douxcemoman.fr</span>
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BookingConfirmation;
